import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import IAdminCustomer from '../types/IAdminCustomer';
import AdminCustomerService from '../services/AdminCustomerService';
import IPaginationState from '../../types/state/IPaginationState';

const NAME = 'AdminCustomer';

const getCustomer = createAsyncThunk(
    `${NAME}/getCustomer`,
    AdminCustomerService.getCustomer,
);
const updateCustomer = createAsyncThunk(
    `${NAME}/updateCustomer`,
    AdminCustomerService.updateCustomer,
);
const getCustomers = createAsyncThunk(
    `${NAME}/getCustomers`,
    AdminCustomerService.getCustomers,
);
const createCustomer = createAsyncThunk(
    `${NAME}/createCustomer`,
    AdminCustomerService.createCustomer,
);

export type AdminCustomerState = {
    list: IPaginationState<IAdminCustomer[]>;
    detail: IBaseState<IAdminCustomer | null>;
};

const initialState: AdminCustomerState = {
    list: {
        data: [],
        loading: false,
        pagination: { page: 1 },
    },
    detail: {
        data: null,
        loading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            getCustomer.fulfilled,
            (state: AdminCustomerState, action) => {
                state.detail.data = action.payload.data;
            },
        );
        builder.addCase(
            updateCustomer.fulfilled,
            (state: AdminCustomerState, action) => {
                if (state.detail.data) {
                    state.detail.data = {
                        ...state.detail.data,
                        ...action.meta.arg,
                    };
                }
            },
        );
        builder.addCase(getCustomers.pending, (state: AdminCustomerState) => {
            state.list.loading = true;
            state.list.data = [];
        });
        builder.addCase(
            getCustomers.fulfilled,
            (state: AdminCustomerState, action) => {
                state.list.data = action.payload.data;
                state.list.pagination = (action.payload as any).pagination;
            },
        );

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(getCustomer.pending, updateCustomer.pending),
            (state: AdminCustomerState) => {
                state.detail.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getCustomer.fulfilled,
                updateCustomer.fulfilled,
                getCustomer.rejected,
                updateCustomer.rejected,
            ),
            (state: AdminCustomerState) => {
                state.detail.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getCustomers.fulfilled, getCustomers.rejected),
            (state: AdminCustomerState) => {
                state.list.loading = false;
            },
        );
    },
});

const state = (state: AppState) => state.admin.customer;

const all = createSelector(state, (state: AdminCustomerState) => state);

const AdminCustomerSlice = {
    reducer: slice.reducer,
    actions: {
        getCustomer,
        updateCustomer,
        getCustomers,
        createCustomer,
    },
    state: {
        all,
        detail: createSelector(
            all,
            (state: AdminCustomerState) => state.detail,
        ),
        list: createSelector(all, (state: AdminCustomerState) => state.list),
    },
};

export default AdminCustomerSlice;
